import { useQuery } from "@tanstack/react-query";
import { useEffect, type ReactNode } from "react";

import { api, unwrap } from "@/api/client";
import { ME_KEY, MemberContext, type Me } from "@/auth/member";
import {
  clearSignInAttempts,
  goToSignIn,
  goToSignOut,
  loadSessionStatus,
  signInAgain,
  signInHold,
  type SignInHold,
} from "@/auth/session";
import { Button } from "@/components/ui/button";
import { LogoMark } from "@/components/ui/logo-mark";
import { Callout } from "@/components/ui/panel";

/**
 * `fetch` has no timeout of its own, and this call decides what the whole page shows. An API that
 * accepts the connection but never answers — a dev server whose worker died under `--reload` —
 * would otherwise leave "Signing in…" up for ever instead of saying the API is not answering.
 */
const ME_TIMEOUT_MS = 15_000;

/** Null means "not signed in" — an answer, not a failure, so it is data rather than an error. */
async function loadMe(): Promise<Me | null> {
  let result;
  try {
    result = await api.GET("/me", { signal: AbortSignal.timeout(ME_TIMEOUT_MS) });
  } catch (error) {
    if (error instanceof DOMException && error.name === "TimeoutError") {
      throw new Error(`The API did not answer within ${ME_TIMEOUT_MS / 1000} seconds.`);
    }
    throw error;
  }
  if (result.response.status === 401) return null;
  return unwrap(result);
}

/**
 * Asks the auth server, from the browser, whether the session is still live — on load and on
 * every return to the tab, which is exactly when a screen left untouched is about to be trusted
 * again. A cookie cannot know it was revoked elsewhere; this is the only check that sees it.
 * On localhost there is nothing to ask (see `loadSessionStatus`), so this does nothing there.
 */
function useSessionWatch(): void {
  const check = useQuery({
    queryKey: ["session-check-url"],
    queryFn: async () => unwrap(await api.GET("/open/session")).check_url,
    staleTime: Infinity,
  });
  const status = useQuery({
    queryKey: ["session-status", check.data],
    queryFn: () => loadSessionStatus(check.data ?? null),
    enabled: check.isSuccess,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
  const ended = status.data ? !status.data.isLoggedIn || status.data.isTokenRevoked : false;
  useEffect(() => {
    if (ended) goToSignIn();
  }, [ended]);
}

/** The one card every page of the sign-in journey uses, since none of them has the app shell. */
export function AuthScreen({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="bg-bank-canvas grid h-full place-items-center p-6">
      <div className="border-bank-border bg-bank-surface w-full max-w-md space-y-4 rounded-lg border p-6">
        <div className="flex items-center gap-3">
          <span className="bg-bank-brand text-bank-on-brand grid size-10 shrink-0 place-items-center rounded-xl">
            <LogoMark className="size-[26px]" />
          </span>
          <div>
            <p className="text-bank-muted font-mono text-[10.5px] tracking-[0.07em] uppercase">
              Question Bank
            </p>
            <h1 className="text-base font-semibold tracking-tight">{title}</h1>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

const HELD: Record<SignInHold, { title: string; body: string }> = {
  signed_out: {
    title: "Signed out",
    body: "You have signed out of the Question Bank. Your Workspace session is untouched.",
  },
  auth_error: {
    title: "Sign-in did not complete",
    body: "Workspace sent you back without a usable session. Try again; if it keeps happening, the API log says why.",
  },
  looping: {
    title: "Sign-in is not sticking",
    body: "You have signed in several times without the session holding — the browser is dropping its cookies. Open the Question Bank on its https address, or allow cookies for this site.",
  },
};

/**
 * Nothing in the back office renders until `/me` says who this is and that they hold a role.
 * The checks are the API's (core/access.py); this only decides which page explains them.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const me = useQuery({ queryKey: ME_KEY, queryFn: loadMe, retry: 1, staleTime: 5 * 60_000 });
  const hold = me.data === null ? signInHold() : null;

  useEffect(() => {
    if (me.data === null) goToSignIn();
    if (me.data) clearSignInAttempts();
  }, [me.data]);

  if (me.isPending) {
    return (
      <AuthScreen title="Signing in…">
        <p className="text-bank-muted text-sm">Checking your Workspace session.</p>
      </AuthScreen>
    );
  }
  if (me.isError) {
    return (
      <AuthScreen title="The API is not answering">
        <Callout tone="bad">{me.error.message}</Callout>
        <Button size="sm" onClick={() => void me.refetch()}>
          Try again
        </Button>
      </AuthScreen>
    );
  }
  if (me.data === null) {
    if (hold === null) {
      return (
        <AuthScreen title="Signing in…">
          <p className="text-bank-muted text-sm">Taking you to Workspace sign-in.</p>
        </AuthScreen>
      );
    }
    return (
      <AuthScreen title={HELD[hold].title}>
        <p className="text-sm">{HELD[hold].body}</p>
        <Button variant="primary" size="sm" onClick={signInAgain}>
          Sign in
        </Button>
      </AuthScreen>
    );
  }
  if (me.data.role === null) return <NoAccess me={me.data} />;
  return (
    <MemberContext.Provider value={me.data}>
      <Watched>{children}</Watched>
    </MemberContext.Provider>
  );
}

/** Split out so the session check only runs for a member who actually got in. */
function Watched({ children }: { children: ReactNode }) {
  useSessionWatch();
  return children;
}

/** Signed in to Workspace, listed under no role. The member number is the one thing an admin needs. */
function NoAccess({ me }: { me: Me }) {
  return (
    <AuthScreen title="No access yet">
      <p className="text-sm">
        You are signed in as <span className="font-medium">{me.full_name || me.member_no}</span>, but your
        member number is not on any Question Bank role.
      </p>
      <p className="text-bank-muted text-sm">
        Ask an admin to add <span className="text-bank-ink font-mono font-medium">{me.member_no}</span> to the
        admin, maintainer or viewer role, then reload this page.
      </p>
      <div className="flex gap-2">
        <Button size="sm" onClick={() => window.location.reload()}>
          Reload
        </Button>
        <Button size="sm" variant="ghost" onClick={() => void goToSignOut()}>
          Sign out
        </Button>
      </div>
    </AuthScreen>
  );
}
